import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Download } from 'lucide-react';
import { supabase } from '../services/supabase';
import { trackEvent } from '../services/analytics';

const DownloadablePrompts = () => {
  const [markdown, setMarkdown] = useState('');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailErrorMessage, setEmailErrorMessage] = useState('');
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/prompttemplatecontent.md', { cache: 'no-cache' });
        if (!res.ok) throw new Error(`Failed to load prompts (${res.status})`);
        const text = await res.text();
        if (!cancelled) setMarkdown(text);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load prompts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!markdown) return;
    const lines = markdown.split('\n');
    const heads = [];
    let current = null;

    const cleanTitle = (raw) => {
      if (!raw) return '';
      // Remove any bracketed brand tags like [PULSEBOARD]
      let t = raw.replace(/\[.*?\]\s*/g, '').trim();
      // If there's a colon, prefer the part after the colon when it looks like a product type
      if (t.includes(':')) {
        const parts = t.split(':').map(s => s.trim());
        const last = parts[parts.length - 1];
        if (/(Marketplace|Dashboard|Community|Knowledge Base|Chat|Scheduling|Microblog|Downloads)/i.test(last)) {
          t = last;
        } else {
          // fallback: use last if it's meaningfully longer than prefix
          t = last.length >= 8 ? last : t;
        }
      }
      // Normalize multiple spaces
      t = t.replace(/\s{2,}/g, ' ').trim();
      return t;
    };

    const friendlyDescription = (title, body) => {
      const t = title.toLowerCase();
      if (/jewel|aurelia|fine jewelry|necklace|ring/.test(t)) {
        return 'Premium multi‑vendor jewelry marketplace with vendor onboarding, variants, and reviews.';
      }
      if (/digital|downloads|templates|assets|templately/.test(t)) {
        return 'Multi‑vendor digital assets store with instant downloads, licensing, and vendor dashboards.';
      }
      if (/streetwear|independent|apparel|fashion/.test(t)) {
        return 'Bold streetwear marketplace with brand pages, “drops”, size variants, and fast checkout.';
      }
      if (/analytics|dashboard|b2b|kpi|charts/.test(t)) {
        return 'B2B analytics dashboard with KPI cards, rich charts, alerts, and scheduled reports.';
      }
      if (/microblog|community|social|posts/.test(t)) {
        return 'Clean social microblog: posts, follows, likes, comments, profiles, and DMs.';
      }
      if (/knowledge base|docs|q&a|docuchat|chat/.test(t)) {
        return 'Docs Q&A app: upload sources, index content, and chat with citations to originals.';
      }
      if (/booking|appointment|schedule|calendar/.test(t)) {
        return 'Service booking platform with provider profiles, availability calendars, and reminders.';
      }
      // Fallback: extract from "## Goal" block or first lines
      const bodyTrim = body.trim();
      const goalIdx = bodyTrim.indexOf('## Goal');
      if (goalIdx >= 0) {
        const after = bodyTrim.slice(goalIdx + '## Goal'.length);
        const goalBlock = after.split('\n## ')[0] || '';
        const goalLines = goalBlock.split('\n').map(l => l.trim()).filter(Boolean).slice(0, 2);
        if (goalLines.length) return goalLines.join(' ');
      }
      const firstLines = bodyTrim.split('\n').map(l => l.trim()).filter(Boolean).slice(0, 2);
      return firstLines.join(' ');
    };

    const pushCurrent = () => {
      if (!current) return;
      const body = current.buffer.join('\n').trim();
      if (!body) return;
      const cleanedTitle = cleanTitle(current.title);
      const description = friendlyDescription(cleanedTitle, body);
      heads.push({
        title: cleanedTitle,
        text: body,
        description,
      });
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const h1Match = line.match(/^#\s+(.+?)\s*$/);
      if (h1Match) {
        const rawTitle = h1Match[1].trim();
        // Skip generic or duplicate headings
        if (['Prompts template', 'Jewellery e commerce store'].includes(rawTitle)) {
          continue;
        }
        // Heuristic: consider only titled templates (often contain ":" or brackets or ALLCAP words)
        const looksLikeTemplate = /:|\[.*\]|[A-Z]{3,}/.test(rawTitle);
        if (!looksLikeTemplate) {
          // Still allow if line ends with "Marketplace" or "Dashboard" or "Scheduling"
          if (!/(Marketplace|Dashboard|Scheduling|Community|Knowledge Base|Downloads)/i.test(rawTitle)) {
            continue;
          }
        }
        // Start a new section
        if (current) pushCurrent();
        current = { title: rawTitle.replace(/^\[|\]$/g, ''), buffer: [] };
      } else {
        if (current) current.buffer.push(line);
      }
    }
    pushCurrent();

    setSections(heads);
  }, [markdown]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return sections.filter(s =>
      s.title.toLowerCase().includes(q) || s.description.toLowerCase().includes(q)
    );
  }, [sections, query]);

  const startDownload = (section) => {
    const safe = section.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const blob = new Blob([`# ${section.title}\n\n${section.text}`], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safe || 'prompt-template'}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const isValidEmail = (email) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(String(email).toLowerCase());

  const requestEmailThenDownload = (section) => {
    setPendingDownload(section);
    setEmailErrorMessage('');
    const cached = localStorage.getItem('downloadable_prompts_email');
    if (cached && isValidEmail(cached)) {
      // have email already, just record and download
      handleEmailSubmit(cached, section, false);
      return;
    }
    setIsEmailModalOpen(true);
  };

  const handleEmailSubmit = async (emailFromCacheOrInput, section = pendingDownload, closeModal = true) => {
    const email = emailFromCacheOrInput || emailInput;
    if (!isValidEmail(email)) {
      setEmailErrorMessage('Please enter a valid email address');
      return;
    }
    setIsSubmittingEmail(true);
    try {
      localStorage.setItem('downloadable_prompts_email', email);
      try { trackEvent('Downloadable Prompt Email Submitted', { template: section?.title }); } catch (e) {}
      const insertPayload = {
        email,
        template: section?.title || null,
        source: 'downloadable_prompts_page',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        created_at: new Date().toISOString()
      };
      // Store to a dedicated table
      await supabase.from('downloadable_prompt_downloads').insert(insertPayload);
    } catch (err) {
      // Non-fatal: still allow download
      try { trackEvent('Downloadable Prompt Email Submit Error', { message: err?.message || String(err) }); } catch (e) {}
      console.warn('Failed to save email for downloadable prompts:', err);
    } finally {
      setIsSubmittingEmail(false);
      if (closeModal) setIsEmailModalOpen(false);
      // Proceed to download
      if (section) startDownload(section);
      setPendingDownload(null);
      setEmailInput('');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-6xl mx-auto px-6 pt-28 pb-16">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Downloadable Prompt Templates</h1>
          <p className="text-gray-600">Minimal, production-ready prompt templates you can save and use.</p>
          <div className="mt-6 max-w-xl mx-auto">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search templates (e.g., marketplace, dashboard, chat)..."
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-black/10 focus:border-black/20"
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="w-8 h-8 border-2 border-black/30 border-t-black rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-black/60">Loading prompts…</p>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-600">{error}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((s, idx) => (
              <motion.div
                key={s.title + idx}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: Math.min(idx * 0.03, 0.3) }}
                className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm flex flex-col"
              >
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{s.title}</h3>
                  <p className="text-sm text-gray-600 line-clamp-4">{s.description}</p>
                </div>
                <div className="pt-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => requestEmailThenDownload(s)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-black text-white font-medium hover:bg-black/90"
                  >
                    <Download className="w-4 h-4" />
                    Download prompt
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
      
      {/* Minimal Email Capture Modal */}
      {isEmailModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setIsEmailModalOpen(false)}></div>
          <div className="relative z-10 w-full max-w-md mx-auto rounded-2xl bg-white p-6 md:p-7 shadow-xl border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-semibold tracking-wide text-vibe-cyan bg-vibe-cyan/10 px-2.5 py-1 rounded-full">IT'S FREE</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Get this prompt</h3>
            <p className="text-gray-600 mb-5">Enter your email to download the template instantly.</p>
            <div className="space-y-4">
              <div>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-xl border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                  required
                />
                {emailErrorMessage ? (
                  <div className="text-sm text-red-600 mt-2">{emailErrorMessage}</div>
                ) : null}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  disabled={isSubmittingEmail}
                  onClick={() => handleEmailSubmit(undefined, pendingDownload)}
                  className="inline-flex items-center justify-center bg-black text-white px-5 py-3 rounded-xl font-semibold hover:bg-gray-800 transition-all duration-200 disabled:opacity-70"
                >
                  {isSubmittingEmail ? 'Preparing…' : 'Download now'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEmailModalOpen(false)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  Maybe later
                </button>
              </div>
              <p className="text-xs text-gray-500">No spam. We may send occasional product updates.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DownloadablePrompts;


